/**
 * Funding-source type catalog: the asset/account types a consumer or advisor can pick, their labels,
 * the mapping to the backend `accountType`, field labels and calculation explanations.
 *
 * Flow-neutral: guided planning, the D2C self-funding sequence, the self-funding details modal and the
 * PDF report all read it. It imports nothing from any flow host. Moved here unchanged from
 * guided planning's wizard config.
 */

export const ASSET_TYPES = [
  { id: 'roth-ira', label: 'Roth IRA' },
  { id: 'roth-401k', label: 'Roth 401(k)' },
  { id: 'traditional-ira', label: 'Traditional IRA' },
  { id: 'traditional-401k', label: 'Traditional 401(k)' },
  { id: 'hsa', label: 'HSA Account' },
  { id: 'brokerage', label: 'Brokerage Account' },
  { id: 'home-equity', label: 'Home Equity' },
  { id: 'retirement-income', label: 'Retirement Income' },
  { id: 'pension', label: 'Pension' },
  { id: 'social-security-income', label: 'Social Security Income' },
  { id: 'existing-ltc-policy', label: 'Existing LTC Coverage' },
] as const;

export type AssetTypeId = (typeof ASSET_TYPES)[number]['id'];

/** Asset types treated as income streams (no compounding, paid over care period). */
export const INCOME_STREAM_ASSET_TYPE_IDS: readonly AssetTypeId[] = [
  'retirement-income',
  'pension',
  'social-security-income',
];

export function isIncomeStreamAssetTypeId(assetTypeId: AssetTypeId): boolean {
  return INCOME_STREAM_ASSET_TYPE_IDS.includes(assetTypeId);
}

/**
 * Maps guided planning asset IDs (kebab-case) to the Redux/backend accountType format (camelCase).
 */
export const ASSET_TYPE_TO_ACCOUNT_TYPE: Record<AssetTypeId, string> = {
  'roth-ira': 'rothIra',
  'roth-401k': 'roth401k',
  'traditional-ira': 'traditionalIra',
  'traditional-401k': 'traditional401k',
  'hsa': 'hsa',
  'brokerage': 'brokerage',
  'home-equity': 'homeEquity',
  'retirement-income': 'retirementIncome',
  'pension': 'pension',
  'social-security-income': 'socialSecurity',
  'existing-ltc-policy': 'existingLtcPolicy',
};

// ─────────────────────────────────────────────────────────────
// FIELD LABELS (shared between guided planning and self-funding modal)
// ─────────────────────────────────────────────────────────────

export interface AssetFieldLabels {
  currentValue: { label: string; placeholder: string };
  yearlyContribution: { label: string; placeholder: string };
  rateOfReturn: { label: string; placeholder: string };
  taxRate: { label: string; placeholder: string };
  taxRateHint?: string;
  contributionStop: string;
}

export const DEFAULT_FIELD_LABELS: AssetFieldLabels = {
  currentValue: { label: 'Current balance', placeholder: 'Enter current balance' },
  yearlyContribution: { label: 'Assumed yearly contribution', placeholder: 'Enter yearly amount' },
  rateOfReturn: { label: 'Assumed annual growth', placeholder: 'Enter annual % assumption' },
  taxRate: { label: 'Est. tax rate on withdrawal', placeholder: 'Enter estimated tax %' },
  contributionStop: 'End contributions early',
};

const ASSET_FIELD_LABELS: Partial<Record<AssetTypeId, Partial<AssetFieldLabels>>> = {
  'roth-ira': {
    taxRate: { label: 'Est. tax rate on withdrawal', placeholder: '0% only if qualified' },
    taxRateHint:
      'Qualified Roth withdrawals (age 59½+, account open 5+ years) are typically tax-free. Enter 0% if qualified, or your expected rate for early withdrawals.',
  },
  'roth-401k': {
    taxRate: { label: 'Est. tax rate on withdrawal', placeholder: '0% only if qualified' },
    taxRateHint:
      'Qualified Roth 401(k) withdrawals (age 59½+, account open 5+ years) are typically tax-free. Enter 0% if qualified, or your expected rate for early withdrawals.',
  },
  'traditional-ira': {
    taxRate: { label: 'Est. income tax rate on withdrawal', placeholder: 'Enter estimated tax %' },
  },
  'traditional-401k': {
    taxRate: { label: 'Est. income tax rate on withdrawal', placeholder: 'Enter estimated tax %' },
  },
  'hsa': {
    taxRate: { label: 'Est. tax rate on withdrawal', placeholder: '0% if qualified expense' },
    taxRateHint:
      'HSA withdrawals for qualified medical expenses (including most LTC costs) are tax-free. Enter 0% if used for qualified expenses, or your expected rate otherwise.',
  },
  'brokerage': {
    currentValue: { label: 'Current account value', placeholder: 'Enter current balance' },
    taxRate: { label: 'Est. tax rate on gain', placeholder: 'Enter rate on gains only' },
    contributionStop: 'Stop adding funds',
  },
  'home-equity': {
    currentValue: { label: 'Current available equity', placeholder: 'Enter current home equity' },
    yearlyContribution: {
      label: 'Yearly equity added',
      placeholder: 'Principal paydown or added equity',
    },
    rateOfReturn: { label: 'Assumed home value growth', placeholder: 'Enter annual home growth %' },
    taxRate: {
      label: 'Est. cost / tax to access equity',
      placeholder: '0% if borrowing; otherwise estimate',
    },
    contributionStop: 'Stop adding equity',
  },
  'retirement-income': {
    currentValue: {
      label: 'Projected annual income at LTC start age',
      placeholder: 'e.g. $50,000',
    },
    taxRate: { label: 'Est. tax rate on payments', placeholder: 'Enter estimated tax %' },
    rateOfReturn: {
      label: 'Expected annual growth',
      placeholder: 'e.g. 2% for cost-of-living adjustments',
    },
  },
  'pension': {
    currentValue: {
      label: 'Expected annual pension income',
      placeholder: 'e.g. $30,000',
    },
    taxRate: { label: 'Est. tax rate on payments', placeholder: 'Enter estimated tax %' },
    rateOfReturn: {
      label: 'Expected annual growth',
      placeholder: 'e.g. 2% for cost-of-living adjustments',
    },
  },
  'social-security-income': {
    currentValue: {
      label: 'Expected annual social security income',
      placeholder: 'e.g. $24,000',
    },
    taxRate: { label: 'Est. tax rate on payments', placeholder: 'Enter estimated tax %' },
    rateOfReturn: {
      label: 'Expected annual growth',
      placeholder: 'e.g. 2% for cost-of-living adjustments',
    },
  },
};

export function getFieldLabels(assetTypeId: AssetTypeId): AssetFieldLabels {
  const overrides = ASSET_FIELD_LABELS[assetTypeId];
  if (!overrides) return { ...DEFAULT_FIELD_LABELS };
  return {
    currentValue: overrides.currentValue ?? DEFAULT_FIELD_LABELS.currentValue,
    yearlyContribution: overrides.yearlyContribution ?? DEFAULT_FIELD_LABELS.yearlyContribution,
    rateOfReturn: overrides.rateOfReturn ?? DEFAULT_FIELD_LABELS.rateOfReturn,
    taxRate: overrides.taxRate ?? DEFAULT_FIELD_LABELS.taxRate,
    taxRateHint: overrides.taxRateHint,
    contributionStop: overrides.contributionStop ?? DEFAULT_FIELD_LABELS.contributionStop,
  };
}

// ─────────────────────────────────────────────────────────────
// CALCULATION FORMULA DESCRIPTIONS (per account type)
// ─────────────────────────────────────────────────────────────

export interface CalcFormulaInfo {
  title: string;
  description: string;
  taxExplanation: string;
}

const DEFAULT_FORMULA_INFO: CalcFormulaInfo = {
  title: 'Investment Growth Projection',
  description:
    'Your account is projected using a year-by-year simulation that grows the balance, adds contributions, and subtracts withdrawals each year until the predicted long-term care start age.',
  taxExplanation: 'Tax is applied to investment gains only (growth above your cost basis).',
};

const CALC_FORMULA_BY_ASSET_TYPE: Partial<Record<AssetTypeId, Partial<CalcFormulaInfo>>> = {
  'traditional-ira': {
    taxExplanation:
      'Traditional IRA contributions are pre-tax, so the entire balance is taxed as ordinary income upon withdrawal — not just the gains.',
  },
  'traditional-401k': {
    taxExplanation:
      'Traditional 401(k) contributions are pre-tax, so the entire balance is taxed as ordinary income upon withdrawal — not just the gains.',
  },
  'roth-ira': {
    taxExplanation:
      'Qualified Roth IRA withdrawals (age 59½+, account open 5+ years) are tax-free. Only enter a tax rate if you plan to withdraw before qualifying.',
  },
  'roth-401k': {
    taxExplanation:
      'Qualified Roth 401(k) withdrawals (age 59½+, account open 5+ years) are tax-free. Only enter a tax rate if you plan to withdraw before qualifying.',
  },
  'hsa': {
    taxExplanation:
      'HSA withdrawals for qualified medical expenses — including most long-term care costs — are tax-free. Tax only applies if used for non-qualified expenses.',
  },
  'brokerage': {
    taxExplanation:
      'Tax is applied to capital gains only — the growth above your original cost basis.',
  },
  'home-equity': {
    title: 'Home Equity Projection',
    description:
      'Your available home equity is projected using a year-by-year simulation that grows the equity value, adds yearly equity contributions (e.g. principal paydown), and subtracts any withdrawals.',
    taxExplanation:
      'The estimated cost or tax to access equity is applied to the gains portion. If borrowing against equity (e.g. HELOC), this may be 0%.',
  },
  'retirement-income': {
    title: 'Income Stream Projection',
    description:
      'Retirement income (e.g. Social Security, pension, rental income) is projected year-by-year. The expected annual growth compounds each year from the reference age through the end of the care period.',
    taxExplanation:
      'The full income amount is taxed as ordinary income — there is no cost basis to deduct.',
  },
  'pension': {
    title: 'Pension Income Projection',
    description:
      'Pension income is projected year-by-year. The expected annual growth compounds each year from the reference age through the end of the care period.',
    taxExplanation:
      'Pension payments are taxed as ordinary income — there is no cost basis to deduct.',
  },
  'social-security-income': {
    title: 'Social Security Income Projection',
    description:
      'Social Security income is projected year-by-year. The expected annual growth compounds each year from the reference age through the end of the care period.',
    taxExplanation:
      'Social Security payments are taxed as ordinary income — there is no cost basis to deduct.',
  },
};

export function getCalcFormulaInfo(assetTypeId: AssetTypeId): CalcFormulaInfo {
  const overrides = CALC_FORMULA_BY_ASSET_TYPE[assetTypeId];
  if (!overrides) return { ...DEFAULT_FORMULA_INFO };
  return {
    title: overrides.title ?? DEFAULT_FORMULA_INFO.title,
    description: overrides.description ?? DEFAULT_FORMULA_INFO.description,
    taxExplanation: overrides.taxExplanation ?? DEFAULT_FORMULA_INFO.taxExplanation,
  };
}

/** Asset types available for self-funding (excludes existing LTC policy). */
export const SELF_FUNDING_ASSET_TYPES = ASSET_TYPES.filter(t => t.id !== 'existing-ltc-policy');

/** Asset types gated behind the `retirementIncomeExpanded` flag. */
const EXPANDED_RETIREMENT_INCOME_ASSET_IDS: readonly AssetTypeId[] = [
  'pension',
  'social-security-income',
];

/**
 * Removes pension and social security income from an asset list when the
 * `retirementIncomeExpanded` flag is disabled.
 */
export function filterForRetirementIncomeExpanded<T extends { id: AssetTypeId }>(
  types: readonly T[],
  isEnabled: boolean | undefined,
): T[] {
  if (isEnabled) return [...types];
  return types.filter(t => !EXPANDED_RETIREMENT_INCOME_ASSET_IDS.includes(t.id));
}

/** Reverse mapping: accountType (camelCase) → AssetTypeId (kebab-case). */
export function getAssetTypeIdFromAccountType(
  accountType: string | null | undefined,
): AssetTypeId | null {
  if (!accountType) return null;
  const entry = Object.entries(ASSET_TYPE_TO_ACCOUNT_TYPE).find(([, v]) => v === accountType);
  return entry ? (entry[0] as AssetTypeId) : null;
}
